/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { HelpOutline, NotInterested, Schedule, TaskAltOutlined } from "@mui/icons-material";
import { Chip, ChipProps } from "@mui/material";
import { FC, type JSX } from "react";

import TestIds from "../../../../testIds";
import { ActivityStatus } from "../../api/types/types";

const DefaultStatusBadgeSx = {
  fontWeight: 700,
  width: "fit-content",
  cursor: "default",
};

export const StatusDisplayDictionary = {
  [ActivityStatus.Open]: {
    // TODO: update this once this color is added to the theme
    sx: {
      backgroundColor: "#CCDFF1" as const,
      color: "#1E5B94" as const,
      ...DefaultStatusBadgeSx,
    },
    icon: <Schedule color="primary" sx={{ color: "#1E5B94" as const }} />,
    label: "PLANNED",
    "data-testid": TestIds.feat.activities.statusBadge.planned,
  },
  [ActivityStatus.Completed]: {
    sx: {
      backgroundColor: "#DCF0E6" as const,
      color: "#137746" as const,
      ...DefaultStatusBadgeSx,
    },
    icon: <TaskAltOutlined color="success" sx={{ color: "#137746" as const }} />,
    label: "COMPLETED",
    "data-testid": TestIds.feat.activities.statusBadge.completed,
  },
  [ActivityStatus.Canceled]: {
    color: "default" as const,
    sx: { color: "#6B6F7A" as const, ...DefaultStatusBadgeSx },
    icon: <NotInterested color="disabled" sx={{ color: "#6B6F7A" as const }} />,
    label: "CANCELLED",
    "data-testid": TestIds.feat.activities.statusBadge.canceled,
  },
  [ActivityStatus.Scheduled]: {
    // TODO: update this once this color is added to the theme
    sx: {
      backgroundColor: "#CCDFF1" as const,
      color: "#1E5B94" as const,
      ...DefaultStatusBadgeSx,
    },
    icon: <Schedule color="primary" sx={{ color: "#1E5B94" as const }} />,
    label: "PLANNED",
    "data-testid": TestIds.feat.activities.statusBadge.planned,
  },
  unknown: {
    color: "default" as const,
    icon: <HelpOutline />,
    label: "Unknown",
    "data-testid": TestIds.feat.activities.statusBadge.unknown,
  },
} as const;

export interface StatusBadgeProps extends Pick<ChipProps, "size"> {
  status: ActivityStatus;
}

const StatusBadge: FC<StatusBadgeProps> = ({ status, size }): JSX.Element => {
  const statusProps = Object.values(ActivityStatus).includes(status)
    ? StatusDisplayDictionary[status]
    : StatusDisplayDictionary.unknown;

  return <Chip {...statusProps} size={size} className="farmlands-v2" />;
};

export default StatusBadge;
